import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FilePdf, DownloadSimple, ClockCounterClockwise, MapPin, User } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso.slice(0, 19); }
};

const fmtMoney = (n) => "$" + Math.round(Number(n) || 0).toLocaleString();

const shortUa = (ua) => {
  if (!ua) return "—";
  // Extract the meaningful piece of the user agent
  const m = ua.match(/\((?:iPhone|iPad|Android|Windows|Macintosh)[^)]*\)/);
  if (m) return m[0].replace(/[()]/g, "");
  return ua.slice(0, 60) + (ua.length > 60 ? "…" : "");
};

const PRODUCT_LABEL = {
  iul: "IUL",
  annuity: "FIA",
  whole_life: "Whole Life",
  term: "Term",
  final_expense: "Final Expense",
};

export default function AdminPdfExports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/pdf-exports");
        setItems(r.data?.exports || []);
      } catch (e) {
        toast.error("Could not load PDF exports.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadPdf = async (id) => {
    setDownloadingId(id);
    try {
      const res = await api.get(`/admin/pdf-exports/${id}/regenerate`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `exodus-export-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  const q = filter.toLowerCase().trim();
  const filtered = q
    ? items.filter((it) =>
        (it.client_name || "").toLowerCase().includes(q) ||
        (it.client_email || "").toLowerCase().includes(q) ||
        (it.product || "").toLowerCase().includes(q) ||
        (it.state || "").toLowerCase().includes(q) ||
        (it.top_carrier || "").toLowerCase().includes(q)
      )
    : items;

  return (
    <DashboardLayout>
      <div data-testid="admin-pdf-exports-page" className="p-6 lg:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <FilePdf size={16} weight="fill" className="text-exodus-gold" /> Client PDF Exports
            </p>
            <h1 className="font-display text-4xl mt-2">
              Every calculator PDF a client downloaded.
            </h1>
            <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
              Silent copies of every PDF export the client saw. Includes their name, email,
              location, device, and the top carrier match. Clients do not see this dashboard.
            </p>
          </div>
          <div className="text-right">
            <p className="eyebrow text-xs text-exodus-muted">Total exports</p>
            <p className="font-display text-4xl gold-text">{items.length}</p>
          </div>
        </div>

        <input
          data-testid="admin-pdf-filter"
          placeholder="Filter by name, email, product, state, or carrier..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full bg-exodus-elevated border border-exodus-border px-4 py-3 text-sm text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors mb-6"
        />

        {loading ? (
          <p className="text-exodus-muted">Loading exports…</p>
        ) : filtered.length === 0 ? (
          <div className="border border-exodus-border p-10 text-center">
            <p className="text-exodus-muted">
              {items.length === 0
                ? "No client exports yet. When a client hits Export PDF on the calculator, it will show up here — with a full copy attached to your email."
                : "No exports match your filter."}
            </p>
          </div>
        ) : (
          <div className="border border-exodus-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-exodus-elevated">
                <tr className="text-left eyebrow text-xs text-exodus-muted">
                  <th className="p-3">Timestamp</th>
                  <th className="p-3">Client</th>
                  <th className="p-3">Product</th>
                  <th className="p-3">Age · State</th>
                  <th className="p-3">Monthly</th>
                  <th className="p-3">Top carrier match</th>
                  <th className="p-3">Rating class</th>
                  <th className="p-3">IP · Device</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`admin-pdf-row-${it.id}`}
                    className="border-t border-exodus-border hover:bg-exodus-elevated/40 transition-colors"
                  >
                    <td className="p-3 text-exodus-muted whitespace-nowrap">
                      <ClockCounterClockwise size={12} className="inline mr-1 opacity-60" />
                      {fmtDate(it.created_at)}
                    </td>
                    <td className="p-3">
                      <p className="text-exodus-text font-medium flex items-center gap-1">
                        <User size={12} className="text-exodus-muted" /> {it.client_name || "Guest"}
                      </p>
                      <p className="text-xs text-exodus-muted">{it.client_email || "—"}</p>
                    </td>
                    <td className="p-3">
                      <span className="text-xs eyebrow px-2 py-1 border border-exodus-gold-deep/50 text-exodus-gold">
                        {PRODUCT_LABEL[it.product] || it.product}
                      </span>
                    </td>
                    <td className="p-3">
                      <p>{it.age || "—"}</p>
                      <p className="text-xs text-exodus-muted">
                        <MapPin size={10} className="inline" /> {it.state || "—"}
                      </p>
                    </td>
                    <td className="p-3 text-exodus-text">{fmtMoney(it.monthly_premium)}</td>
                    <td className="p-3 gold-text font-medium">{it.top_carrier || "—"}</td>
                    <td className="p-3 text-exodus-muted">{it.predicted_rating_class || "—"}</td>
                    <td className="p-3">
                      <p className="text-xs font-mono text-exodus-muted">{it.ip_address || "—"}</p>
                      <p className="text-[10px] text-exodus-muted">{shortUa(it.user_agent)}</p>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        data-testid={`admin-pdf-dl-${it.id}`}
                        onClick={() => downloadPdf(it.id)}
                        disabled={downloadingId === it.id}
                        className="inline-flex items-center gap-1 text-xs border border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg px-3 py-2 transition-colors disabled:opacity-40"
                      >
                        <DownloadSimple size={12} />
                        {downloadingId === it.id ? "Loading…" : "PDF"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
